import { api, apiRequest, buildWcUrl, getPaginationFromHeaders, PaginationHeaders } from './api';
import { Category, QueryParams } from '../types/woocommerce';
import { isCategoryFrench } from '../config/french-filter';

export interface CategoriesResponse {
  categories: Category[];
  pagination: PaginationHeaders;
}

export interface CategoryQueryParams extends QueryParams {
  hide_empty?: boolean;
  product?: number;
}

// Service pour les catégories
export const categoriesService = {
  // Récupérer toutes les catégories avec pagination
  async getCategories(params?: CategoryQueryParams): Promise<CategoriesResponse> {
    const response = await api.get<Category[]>(buildWcUrl('products/categories'), { params });
    
    // Filtrer les catégories françaises
    const filteredCategories = response.data.filter(isCategoryFrench);
    
    return {
      categories: filteredCategories,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer une catégorie par ID
  async getCategory(id: number): Promise<Category> {
    return apiRequest.get<Category>(buildWcUrl(`products/categories/${id}`));
  },

  // Récupérer une catégorie par slug
  async getCategoryBySlug(slug: string): Promise<Category> {
    const categories = await apiRequest.get<Category[]>(buildWcUrl('products/categories'), {
      params: { slug },
    });
    if (categories.length === 0) {
      throw new Error('Catégorie non trouvée');
    }
    return categories[0];
  },

  // Récupérer les catégories parentes (niveau supérieur)
  async getParentCategories(params?: CategoryQueryParams): Promise<CategoriesResponse> {
    const response = await api.get<Category[]>(buildWcUrl('products/categories'), {
      params: { ...params, parent: 0 },
    });
    
    // Filtrer les catégories françaises
    const filteredCategories = response.data.filter(isCategoryFrench);
    
    return {
      categories: filteredCategories,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer les sous-catégories d'une catégorie
  async getSubCategories(parentId: number, params?: CategoryQueryParams): Promise<CategoriesResponse> {
    const response = await api.get<Category[]>(buildWcUrl('products/categories'), {
      params: { ...params, parent: parentId },
    });
    return {
      categories: response.data,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer l'arbre complet des catégories
  async getCategoryTree(): Promise<Category[]> {
    const allCategories = await this.getAllCategories();
    return this.buildCategoryTree(allCategories);
  },

  // Récupérer toutes les catégories (sans pagination)
  async getAllCategories(): Promise<Category[]> {
    let allCategories: Category[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await api.get<Category[]>(buildWcUrl('products/categories'), {
        params: { page, per_page: 100 }
      });
      const filteredCategories = response.data.filter(isCategoryFrench);
      allCategories = [...allCategories, ...filteredCategories];
      hasMore = page < getPaginationFromHeaders(response.headers).totalPages;
      page++;
    }

    return allCategories;
  },

  // Construire l'arbre de catégories
  buildCategoryTree(categories: Category[]): Category[] {
    const categoryMap = new Map<number, Category & { children?: Category[] }>();
    const tree: (Category & { children?: Category[] })[] = [];

    // Créer une map pour un accès rapide
    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Construire l'arbre
    categories.forEach(cat => {
      const category = categoryMap.get(cat.id)!;
      if (cat.parent === 0) {
        tree.push(category);
      } else {
        const parent = categoryMap.get(cat.parent);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(category);
        }
      }
    });

    return tree;
  },

  // Obtenir le chemin complet d'une catégorie (breadcrumb)
  async getCategoryPath(categoryId: number): Promise<Category[]> {
    const path: Category[] = [];
    let currentId = categoryId;

    while (currentId !== 0) {
      const category = await this.getCategory(currentId);
      path.unshift(category);
      currentId = category.parent;
    }

    return path;
  },

  // Rechercher des catégories
  async searchCategories(search: string, params?: CategoryQueryParams): Promise<CategoriesResponse> {
    const response = await api.get<Category[]>(buildWcUrl('products/categories'), {
      params: { ...params, search },
    });
    return {
      categories: response.data,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Vérifier si une catégorie a des sous-catégories
  async hasSubCategories(categoryId: number): Promise<boolean> {
    const response = await this.getSubCategories(categoryId, { per_page: 1 });
    return response.categories.length > 0;
  },

  // Récupérer les catégories avec le nombre de produits
  async getCategoriesWithProductCount(params?: CategoryQueryParams): Promise<CategoriesResponse> {
    // Par défaut, WooCommerce inclut le count
    return this.getCategories({ ...params, hide_empty: false });
  },

  // Récupérer uniquement les catégories non vides
  async getNonEmptyCategories(params?: CategoryQueryParams): Promise<CategoriesResponse> {
    return this.getCategories({ ...params, hide_empty: true });
  },
};
